import { useRef, useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { BellIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useNotifications } from "../../../context/NotificationContext";
import NotificationItem from "./NotificationItem";
import type { AppNotification } from "../types";
import { useLanguage } from "../../../context/LanguageContext";

export default memo(function NotificationBell() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleClick = async (n: AppNotification) => {
    if (!n.isRead) await markRead(n.id);
    setOpen(false);
    if (n.data?.brief_id) {
      if (n.type === "casting_brief_created") {
        navigate(`/admin/briefs?brief_id=${n.data.brief_id}`);
      } else {
        navigate(`/casting-brief/${n.data.brief_id}`);
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      {/* Bell button */}
      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors group"
        aria-label={t("notifications.title")}
      >
        <BellIcon className="w-5 h-5 text-gray-700 group-hover:text-[#b2d12e] transition-colors" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key="badge"
              initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              className="absolute -top-1 -end-1 bg-red-500 text-white text-xs min-w-[18px] h-[18px] rounded-full flex items-center justify-center font-bold px-1"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute end-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <BellIcon className="w-4 h-4 text-[#b2d12e]" /> {t("notifications.title")}
                {unreadCount > 0 && (
                  <span className="bg-[#b2d12e]/20 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    {t("notifications.newCount", { count: unreadCount })}
                  </span>
                )}
              </span>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#b2d12e] hover:underline font-medium">
                  {t("notifications.markAllRead")}
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-400">{t("common.loading")}</div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <BellIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">{t("notifications.noNotificationsYet")}</p>
                </div>
              ) : (
                notifications.slice(0, 10).map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={handleClick} />
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <button
                onClick={() => { setOpen(false); navigate("/notifications"); }}
                className="w-full py-3 text-xs font-semibold text-[#b2d12e] hover:bg-gray-50 transition-colors border-t border-gray-100"
              >
                {t("notifications.viewAll")}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
